import { FC, useState } from 'react';
import { Button, Divider, Dropdown, Input, Popconfirm, Popover, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
    TbCheck,
    TbClock,
    TbDatabase,
    TbDots,
    TbLayoutRows,
    TbPencil,
    TbRestore,
    TbTable,
    TbTextSize,
    TbTrash,
    TbVariable,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CompactCollapsible, EmptySection, SmallTag, SmallTooltip } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';

import type {
    VantageDataBlock,
    VantageMetadata,
    VantageQueryHistoryEntry,
    VantageQueryResult,
} from '../../../../../types';
import VantageCodeBlock from '../../../../../VantageCodeBlock';
import { formatSqlQuery } from '../../../../helpers/query';
import { CollapsibleSection, VantagePropertyRow } from '../VantageSettingsPrimitives';
import { formatRuntime } from '../VantageSettingsPrimitives/helpers';

interface MetadataTabProps {
    code: string;
    dataBlocks: VantageDataBlock[];
    error: string | null;
    metadata: VantageMetadata;
    queryHistory: VantageQueryHistoryEntry[];
    referencedTableCount: number;
    result: VantageQueryResult | null;
    runtimeMs: number | null;
    deleteDataBlock: (dataBlockId: string) => Promise<void>;
    getDataBlockReferenceNameError: (dataBlockId: string, referenceName: string) => string | undefined;
    restoreQuery: (entry: VantageQueryHistoryEntry | VantageDataBlock) => void;
    updateDataBlockReferenceName: (dataBlockId: string, referenceName: string) => Promise<void>;
}

interface QueryDisclosureProps {
    columnCount?: number;
    rowCount: number;
    runtimeMs: number;
    sql: string;
    onOpenChange?: (open: boolean) => void;
}

const QueryDisclosure: FC<QueryDisclosureProps> = ({ columnCount, rowCount, runtimeMs, sql, onOpenChange }) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(false);

    return (
        <CompactCollapsible
            contentStyle={{ padding: 0 }}
            destroyOnHidden
            headerStyle={{ minHeight: '2rem', padding: 0 }}
            label={
                <small style={{ color: token.colorTextTertiary, fontWeight: 400 }}>
                    {rowCount.toLocaleString()} {rowCount === 1 ? 'row' : 'rows'}
                    {columnCount === undefined
                        ? null
                        : ` · ${columnCount.toLocaleString()} ${columnCount === 1 ? 'column' : 'columns'}`}{' '}
                    · {formatRuntime(runtimeMs)}
                </small>
            }
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                onOpenChange?.(nextOpen);
            }}
            open={open}
            sectionStyle={{ border: 0 }}
        >
            <VantageCodeBlock
                code={formatSqlQuery(sql)}
                language='sql'
                style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    height: 'auto',
                    maxHeight: '14rem',
                    padding: '0.75rem',
                }}
            />
        </CompactCollapsible>
    );
};

interface DatablockActionsDropdownProps {
    dataBlock: VantageDataBlock;
    deleteDataBlock: (dataBlockId: string) => Promise<void>;
    getReferenceNameError: (dataBlockId: string, referenceName: string) => string | undefined;
    updateReferenceName: (dataBlockId: string, referenceName: string) => Promise<void>;
}

const DatablockActionsDropdown: FC<DatablockActionsDropdownProps> = ({
    dataBlock,
    deleteDataBlock,
    getReferenceNameError,
    updateReferenceName,
}) => {
    const { token } = useAntDesignTheme();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [referenceName, setReferenceName] = useState(dataBlock.referenceName);
    const referenceNameError = getReferenceNameError(dataBlock.id, referenceName);
    const saveReferenceName = async () => {
        if (referenceNameError) {
            return;
        }

        setRenameOpen(false);

        try {
            await updateReferenceName(dataBlock.id, referenceName);
        } catch {
            setReferenceName(dataBlock.referenceName);
            setRenameOpen(true);
        }
    };

    const menuItems: MenuProps['items'] = [
        {
            icon: <TbPencil size='1rem' />,
            key: 'rename',
            label: 'Rename',
        },
        {
            danger: true,
            icon: <TbTrash size='1rem' />,
            key: 'delete',
            label: 'Delete',
        },
    ];

    const handleMenuClick: MenuProps['onClick'] = ({ domEvent, key }) => {
        domEvent.stopPropagation();
        setDropdownOpen(false);

        if (key === 'rename') {
            setReferenceName(dataBlock.referenceName);
            setRenameOpen(true);
        } else if (key === 'delete') {
            setDeleteConfirmOpen(true);
        }
    };

    return (
        <Popover
            content={
                <div style={{ width: '17rem' }}>
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            aria-label='Datablock name'
                            // This click-opened naming field is the primary action in the compact editor.
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            onChange={(event) => setReferenceName(event.target.value)}
                            onPressEnter={() => void saveReferenceName()}
                            placeholder='Datablock Name...'
                            size='small'
                            status={referenceNameError ? 'error' : undefined}
                            value={referenceName}
                        />
                        <Button
                            aria-label='Save datablock name'
                            disabled={Boolean(referenceNameError)}
                            icon={<TbCheck size='0.875rem' />}
                            onClick={() => void saveReferenceName()}
                            size='small'
                            type='primary'
                        >
                            Save
                        </Button>
                    </Space.Compact>
                    {referenceNameError ? (
                        <small style={{ color: token.colorError, display: 'block', marginTop: '0.25rem' }}>
                            {referenceNameError}
                        </small>
                    ) : null}
                </div>
            }
            arrow={false}
            destroyOnHidden
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    setReferenceName(dataBlock.referenceName);
                    setRenameOpen(false);
                }
            }}
            open={renameOpen}
            placement='left'
            trigger={[]}
        >
            <span style={{ display: 'inline-flex' }}>
                <Popconfirm
                    cancelText='Cancel'
                    description='References to this Datablock will no longer resolve.'
                    okButtonProps={{ danger: true }}
                    okText='Delete'
                    onCancel={() => setDeleteConfirmOpen(false)}
                    onConfirm={async () => {
                        setDeleteConfirmOpen(false);
                        await deleteDataBlock(dataBlock.id);
                    }}
                    onOpenChange={(nextOpen) => {
                        if (!nextOpen) {
                            setDeleteConfirmOpen(false);
                        }
                    }}
                    open={deleteConfirmOpen}
                    placement='topRight'
                    title='Delete Datablock?'
                    trigger={[]}
                >
                    <span style={{ display: 'inline-flex' }}>
                        <Dropdown
                            menu={{ items: menuItems, onClick: handleMenuClick }}
                            onOpenChange={setDropdownOpen}
                            open={dropdownOpen}
                            placement='bottomRight'
                            trigger={['click']}
                        >
                            <Button aria-label='Datablock actions' icon={<TbDots size='1rem' />} size='small' />
                        </Dropdown>
                    </span>
                </Popconfirm>
            </span>
        </Popover>
    );
};

interface DatablockListItemProps {
    dataBlock: VantageDataBlock;
    deleteDataBlock: (dataBlockId: string) => Promise<void>;
    getReferenceNameError: (dataBlockId: string, referenceName: string) => string | undefined;
    restoreQuery: (entry: VantageDataBlock) => void;
    updateReferenceName: (dataBlockId: string, referenceName: string) => Promise<void>;
}

const DatablockListItem: FC<DatablockListItemProps> = ({
    dataBlock,
    deleteDataBlock,
    getReferenceNameError,
    restoreQuery,
    updateReferenceName,
}) => {
    const { token } = useAntDesignTheme();
    const [queryOpen, setQueryOpen] = useState(false);
    const referenceNameError = getReferenceNameError(dataBlock.id, dataBlock.referenceName);

    return (
        <div
            style={{
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                padding: `0.75rem 0.75rem ${queryOpen ? '0.75rem' : '0.5rem'}`,
                transition: `padding-bottom ${token.motionDurationMid} ${token.motionEaseInOut}`,
            }}
        >
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'space-between',
                    minWidth: 0,
                }}
            >
                <SmallTag
                    compact
                    message={
                        <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}>
                            <span>{`{{ ${dataBlock.referenceName} }}`}</span>
                            <span style={{ color: token.colorTextTertiary }}>array</span>
                        </span>
                    }
                    styles={{
                        marginLeft: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                />
                <Space align='center' size={0} style={{ flex: 'none' }}>
                    <SmallTooltip title='Restore Datablock' placement='top'>
                        <Popconfirm
                            cancelText='Cancel'
                            description='Replace the active editor query and visualization settings with this Datablock.'
                            okText='Restore'
                            onConfirm={() => restoreQuery(dataBlock)}
                            placement='topRight'
                            title='Restore Datablock?'
                        >
                            <Button aria-label='Restore datablock' icon={<TbRestore size='1rem' />} size='small'>
                                Restore
                            </Button>
                        </Popconfirm>
                    </SmallTooltip>
                    <Divider style={{ height: '1rem', margin: '0 0.5rem' }} type='vertical' />
                    <DatablockActionsDropdown
                        dataBlock={dataBlock}
                        deleteDataBlock={deleteDataBlock}
                        getReferenceNameError={getReferenceNameError}
                        updateReferenceName={updateReferenceName}
                    />
                </Space>
            </div>
            {referenceNameError ? (
                <small style={{ color: token.colorError, display: 'block', marginTop: '0.25rem' }}>
                    {referenceNameError}
                </small>
            ) : null}
            <QueryDisclosure
                columnCount={dataBlock.columns.length}
                rowCount={dataBlock.rowCount}
                runtimeMs={dataBlock.runtimeMs}
                sql={dataBlock.sql}
                onOpenChange={setQueryOpen}
            />
        </div>
    );
};

interface QueryHistoryListItemProps {
    entry: VantageQueryHistoryEntry;
    restoreQuery: (entry: VantageQueryHistoryEntry) => void;
}

const QueryHistoryListItem: FC<QueryHistoryListItemProps> = ({ entry, restoreQuery }) => {
    const { token } = useAntDesignTheme();
    const [queryOpen, setQueryOpen] = useState(false);

    return (
        <div
            style={{
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                padding: `0.75rem 0.75rem ${queryOpen ? '0.75rem' : '0.5rem'}`,
                transition: `padding-bottom ${token.motionDurationMid} ${token.motionEaseInOut}`,
            }}
        >
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'space-between',
                }}
            >
                <SmallTag compact message='Session query' styles={{ marginLeft: 0 }} />
                <Button icon={<TbRestore size='1rem' />} onClick={() => restoreQuery(entry)} size='small'>
                    Restore
                </Button>
            </div>
            <QueryDisclosure
                rowCount={entry.rowCount}
                runtimeMs={entry.runtimeMs}
                sql={entry.sql}
                onOpenChange={setQueryOpen}
            />
        </div>
    );
};

const MetadataTab: FC<MetadataTabProps> = ({
    code,
    dataBlocks,
    deleteDataBlock,
    error,
    getDataBlockReferenceNameError,
    metadata,
    queryHistory,
    referencedTableCount,
    restoreQuery,
    result,
    runtimeMs,
    updateDataBlockReferenceName,
}) => (
    <div>
        <CollapsibleSection
            title='Query Breakdown'
            description='Review the active query, governed dependencies, and latest execution.'
            icon={<TbDatabase />}
        >
            <VantagePropertyRow
                label='Characters'
                description='Total characters in the active SQL statement.'
                icon={<TbTextSize />}
            >
                <Typography.Text>{code.length.toLocaleString()}</Typography.Text>
            </VantagePropertyRow>
            <VantagePropertyRow
                label='Tables'
                description='Governed schema tables referenced by the query.'
                icon={<TbTable />}
            >
                <Typography.Text>{referencedTableCount.toLocaleString()}</Typography.Text>
            </VantagePropertyRow>
            <VantagePropertyRow
                label='Variables'
                description='Dynamic variables available to the active query.'
                icon={<TbVariable />}
            >
                <Typography.Text>{metadata.variables.length.toLocaleString()}</Typography.Text>
            </VantagePropertyRow>
            <VantagePropertyRow
                label='Rows'
                description='Rows returned by the latest successful execution.'
                icon={<TbLayoutRows />}
            >
                <Typography.Text>{(result?.rowCount ?? 0).toLocaleString()}</Typography.Text>
            </VantagePropertyRow>
            <VantagePropertyRow
                label='Runtime'
                description='Elapsed time for the latest query execution.'
                icon={<TbClock />}
                last
            >
                <Typography.Text>{formatRuntime(runtimeMs)}</Typography.Text>
            </VantagePropertyRow>
            {error ? <PlatformAlert type='error' showIcon message={error} style={{ marginTop: '0.75rem' }} /> : null}
        </CollapsibleSection>
        <CollapsibleSection
            title='Datablocks'
            description='Restore and manage reusable query results from this editor session.'
            icon={<TbDatabase />}
        >
            {dataBlocks.length ? (
                <Space direction='vertical' size={8} style={{ width: '100%' }}>
                    {dataBlocks.map((dataBlock) => (
                        <DatablockListItem
                            dataBlock={dataBlock}
                            deleteDataBlock={deleteDataBlock}
                            getReferenceNameError={getDataBlockReferenceNameError}
                            key={dataBlock.id}
                            restoreQuery={restoreQuery}
                            updateReferenceName={updateDataBlockReferenceName}
                        />
                    ))}
                </Space>
            ) : queryHistory.length ? (
                <Space direction='vertical' size={8} style={{ width: '100%' }}>
                    {queryHistory.map((entry) => (
                        <QueryHistoryListItem entry={entry} key={entry.id} restoreQuery={restoreQuery} />
                    ))}
                </Space>
            ) : (
                <EmptySection
                    height={128}
                    icon={<TbDatabase size='1.25rem' />}
                    label='No Datablocks'
                    description='Successfully run query results will appear here as reusable Datablocks.'
                />
            )}
        </CollapsibleSection>
    </div>
);

export default MetadataTab;
